import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { reportsApi } from '../services/api';
import ReportVisualization from '../components/reports/ReportVisualization';
import FranchiseReportVisualization from '../components/reports/FranchiseReportVisualization';
import { ResultView as FranchiseV2ResultView } from './FranchiseReportsV2';
import ClinicRoutineReportVisualization from '../components/reports/ClinicRoutineReportVisualization';
import ClinicMultiUnitReportVisualization from '../components/reports/ClinicMultiUnitReportVisualization';
import ClinicFunnelOverviewVisualization from '../components/reports/ClinicFunnelOverviewVisualization';
import LeadGoalProgress from '../components/reports/LeadGoalProgress';
import ClinicDataSection from '../components/ClinicDataSection';
import LeadGoalsBlock from '../components/reports/LeadGoalsBlock';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell,
} from 'recharts';

const fmt = (n, dec = 2) => Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
const fmtCurrency = (n) => `R$ ${fmt(n)}`;
const fmtDate = (d) => { if (!d) return ''; const [y, m, day] = d.split('-'); return `${day}/${m}/${y}`; };

const SOURCE_CONFIG = {
  meta: { label: 'Meta', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  google: { label: 'Google', color: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
};

const GENDER_LABELS = { male: 'Masculino', female: 'Feminino', unknown: 'Não informado' };
const GENDER_COLORS = { male: '#3b82f6', female: '#ec4899', unknown: '#94a3b8' };

const CombinedSnapshotViewer = ({
  data = [], summary = {}, entityName,
  demographics, topCreatives = [], accountsStatus = [], objective = 'ALL',
  startDate, endDate, clinicSection, leadGoalsSection,
  showFunnelAndGoals = true, leadGoal = null, leadGoalAchieved = 0,
}) => {
  const [sortField, setSortField] = React.useState('spend');
  const [sortDir, setSortDir] = React.useState('desc');
  const [sourceFilter, setSourceFilter] = React.useState('ALL');
  const [rankMetric, setRankMetric] = React.useState('conversions');

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const filtered = sourceFilter === 'ALL' ? data : data.filter(r => r.source === sourceFilter);
  const sorted = [...filtered].sort((a, b) => {
    const v = x => Number(x[sortField] || 0);
    return sortDir === 'desc' ? v(b) - v(a) : v(a) - v(b);
  });

  const isMessages = objective === 'MESSAGES';

  return (
    <div className="space-y-8 animate-fade-in">
      <h2 className="font-headline font-extrabold text-3xl text-on-surface">{entityName || 'Relatório Combinado'}</h2>
      {startDate && (
        <div className="flex items-center gap-2 -mt-4">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-bold">
            <span className="material-symbols-outlined text-[16px]">calendar_month</span>
            {fmtDate(startDate)} — {fmtDate(endDate)}
          </span>
          {objective !== 'ALL' && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 text-sm font-bold">
              <span className="material-symbols-outlined text-[16px]">target</span>
              {objective === 'MESSAGES' ? 'Conversas' : objective === 'LEAD_GENERATION' ? 'Geração de Leads' : objective === 'CONVERSIONS' ? 'Conversões' : objective}
            </span>
          )}
        </div>
      )}

      {/* KPIs */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          {[
            { label: 'Investimento Total', value: fmtCurrency(summary.totalSpend), icon: 'payments', color: 'text-blue-500', bg: 'bg-blue-50' },
            { label: 'Impressões', value: Number(summary.totalImpressions || 0).toLocaleString('pt-BR'), icon: 'visibility', color: 'text-indigo-500', bg: 'bg-indigo-50' },
            { label: 'Cliques', value: Number(summary.totalClicks || 0).toLocaleString('pt-BR'), icon: 'ads_click', color: 'text-violet-500', bg: 'bg-violet-50' },
            { label: 'CTR Médio', value: `${fmt(summary.avgCtr)}%`, icon: 'percent', color: 'text-fuchsia-500', bg: 'bg-fuchsia-50' },
            { label: 'CPC Médio', value: fmtCurrency(summary.avgCpc), icon: 'monetization_on', color: 'text-rose-500', bg: 'bg-rose-50' },
            { label: isMessages ? 'Conversas Iniciadas' : 'Conversões', value: fmt(summary.totalConversions, 1), icon: isMessages ? 'chat' : 'check_circle', color: 'text-emerald-500', bg: 'bg-emerald-50' },
          ].map((kpi, idx) => (
            <div key={idx} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm flex flex-col items-center justify-center text-center">
              <div className={`w-12 h-12 rounded-full ${kpi.bg} flex items-center justify-center mb-3`}>
                <span className={`material-symbols-outlined ${kpi.color} text-2xl`}>{kpi.icon}</span>
              </div>
              <h4 className="text-2xl font-black font-headline text-slate-800 tracking-tight leading-none mb-1">{kpi.value}</h4>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1">{kpi.label}</p>
            </div>
          ))}
        </div>
      )}

      {showFunnelAndGoals && (
        leadGoalsSection ? (
          leadGoalsSection
        ) : (leadGoal ? (
          <div className="relative z-10">
            <LeadGoalProgress
              goal={leadGoal}
              achieved={leadGoalAchieved}
              conversionType={objective === 'MESSAGES' ? 'message' : 'lead'}
              readOnly={true}
            />
          </div>
        ) : null)
      )}

      {clinicSection}

      {/* Accounts Status */}
      {accountsStatus.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <span className="material-symbols-outlined text-primary text-[18px]">manage_accounts</span>
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-widest">Status das Contas</h3>
            <span className="ml-auto text-[11px] font-bold text-slate-400">
              {accountsStatus.filter(s => s.status === 'success').length}/{accountsStatus.length} ok
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {accountsStatus.map((acc, i) => {
              const isGoogle = acc.source === 'google';
              const ok = acc.status === 'success';
              return (
                <div key={i} className={`flex items-start gap-3 px-4 py-3 rounded-xl border ${ok ? (isGoogle ? 'border-green-100 bg-green-50/40' : 'border-blue-100 bg-blue-50/40') : 'border-red-100 bg-red-50/40'}`}>
                  <span className={`material-symbols-outlined text-[18px] shrink-0 mt-0.5 ${ok ? (isGoogle ? 'text-green-500' : 'text-blue-500') : 'text-red-400'}`}>
                    {ok ? 'check_circle' : 'error'}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-bold text-slate-700 truncate">{acc.accountName}</p>
                    <p className={`text-[9px] font-bold uppercase tracking-wider mb-0.5 ${isGoogle ? 'text-green-500' : 'text-blue-500'}`}>{isGoogle ? 'Google Ads' : 'Meta Ads'}</p>
                    {ok ? (
                      <p className="text-[10px] text-slate-400">{acc.campaigns} campanha{acc.campaigns !== 1 ? 's' : ''}</p>
                    ) : (
                      <p className="text-[10px] text-red-400 truncate">{acc.error}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Ranking de Campanhas */}
      {data.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <h3 className="font-headline text-xl font-bold text-slate-800 flex items-center gap-2">
              <span className="material-symbols-outlined text-primary text-[22px]">emoji_events</span>
              Ranking de Campanhas
            </h3>
            <div className="flex items-center gap-2 bg-slate-50 p-1 rounded-xl">
              {[
                { key: 'conversions', label: isMessages ? 'Conversas' : 'Conversões' },
                { key: 'spend', label: 'Investimento' },
                { key: 'clicks', label: 'Cliques' },
                { key: 'impressions', label: 'Impressões' },
              ].map(m => (
                <button key={m.key} onClick={() => setRankMetric(m.key)}
                  className={`px-3 py-1.5 rounded-lg text-[11px] uppercase tracking-wider font-bold transition-all ${rankMetric === m.key ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>
          <div className="divide-y divide-slate-50">
            {[...data]
              .sort((a, b) => Number(b[rankMetric] || 0) - Number(a[rankMetric] || 0))
              .slice(0, 10)
              .map((row, i) => {
                const cfg = SOURCE_CONFIG[row.source] || SOURCE_CONFIG.meta;
                const maxVal = Math.max(...data.map(r => Number(r[rankMetric] || 0)));
                const pct = maxVal > 0 ? (Number(row[rankMetric] || 0) / maxVal) * 100 : 0;
                const displayVal = rankMetric === 'spend'
                  ? fmtCurrency(row[rankMetric])
                  : rankMetric === 'conversions'
                    ? fmt(row[rankMetric], 1)
                    : Number(row[rankMetric] || 0).toLocaleString('pt-BR');
                return (
                  <div key={i} className={`flex items-center gap-4 px-6 py-4 hover:bg-slate-50/60 transition-colors ${i === 0 ? 'bg-primary/[0.03]' : ''}`}>
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black shrink-0 ${i === 0 ? 'bg-primary text-white' : i === 1 ? 'bg-slate-200 text-slate-700' : i === 2 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${cfg.color} shrink-0`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`}></span>{cfg.label}
                        </span>
                        <p className="text-xs text-slate-400 truncate">{row.accountName}</p>
                      </div>
                      <p className="text-sm font-bold text-slate-700 truncate">{row.campaignName}</p>
                      <div className="mt-1.5 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-primary/60 rounded-full transition-all" style={{ width: `${pct}%` }}></div>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-base font-black text-slate-800">{displayVal}</p>
                      <p className="text-[10px] text-slate-400 uppercase tracking-wider">
                        {rankMetric === 'spend' ? 'Invest.' : rankMetric === 'conversions' ? (isMessages ? 'Conversas' : 'Conv.') : rankMetric === 'clicks' ? 'Cliques' : 'Impressões'}
                      </p>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Top Criativos */}
      {topCreatives.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-headline text-xl font-bold text-slate-800 flex items-center gap-2">
            <span className="material-symbols-outlined text-primary text-[22px]">auto_awesome</span>
            Top Criativos
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block"></span>Meta Ads
            </span>
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
            {topCreatives.map((ad, i) => (
              <div key={ad.adId || i} className={`bg-white rounded-2xl border shadow-sm overflow-hidden flex flex-col ${i === 0 ? 'border-primary/30 ring-1 ring-primary/20' : 'border-slate-100'}`}>
                <div className="aspect-square bg-slate-100 relative overflow-hidden">
                  {ad.thumbnailUrl || ad.imageUrl ? (
                    <img src={ad.thumbnailUrl || ad.imageUrl} alt={ad.adName} className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="material-symbols-outlined text-slate-300 text-4xl">image</span>
                    </div>
                  )}
                  {i < 3 && (
                    <div className={`absolute top-2 left-2 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black ${i === 0 ? 'bg-primary text-white' : i === 1 ? 'bg-slate-600 text-white' : 'bg-amber-500 text-white'}`}>
                      {i + 1}
                    </div>
                  )}
                </div>
                <div className="p-3 flex-1 flex flex-col gap-1">
                  <div className="flex items-start gap-1">
                    <p className="text-[11px] font-bold text-slate-700 line-clamp-2 leading-tight flex-1">{ad.adName}</p>
                    {ad.previewUrl && (
                      <a href={ad.previewUrl} target="_blank" rel="noopener noreferrer"
                        className="shrink-0 w-5 h-5 flex items-center justify-center rounded-md bg-slate-100 hover:bg-primary hover:text-white text-slate-400 transition-colors mt-0.5"
                        title="Abrir criativo">
                        <span className="material-symbols-outlined text-[13px]">open_in_new</span>
                      </a>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-400 truncate">{ad.accountName}</p>
                  <div className="mt-auto pt-2 grid grid-cols-2 gap-1">
                    <div className="text-center bg-primary/5 rounded-lg p-1.5">
                      <p className="text-[11px] font-black text-primary">{fmt(ad.conversions, 1)}</p>
                      <p className="text-[8px] text-slate-400 uppercase tracking-wider">Conv.</p>
                    </div>
                    <div className="text-center bg-slate-50 rounded-lg p-1.5">
                      <p className="text-[11px] font-black text-slate-700">{fmtCurrency(ad.spend)}</p>
                      <p className="text-[8px] text-slate-400 uppercase tracking-wider">Invest.</p>
                    </div>
                    <div className="text-center bg-slate-50 rounded-lg p-1.5">
                      <p className="text-[11px] font-bold text-slate-600">{ad.conversions > 0 ? fmtCurrency(ad.spend / ad.conversions) : '—'}</p>
                      <p className="text-[8px] text-slate-400 uppercase tracking-wider">{isMessages ? 'C/Conv.' : 'CPL/CPA'}</p>
                    </div>
                    <div className="text-center bg-slate-50 rounded-lg p-1.5">
                      <p className="text-[11px] font-bold text-slate-600">{fmt(ad.ctr)}%</p>
                      <p className="text-[8px] text-slate-400 uppercase tracking-wider">CTR</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Demographics */}
      {demographics && (demographics.byAge?.length > 0 || demographics.byGender?.length > 0) && (
        <div className="space-y-4">
          <h3 className="font-headline text-xl font-bold text-slate-800 flex items-center gap-2">
            Análise Demográfica
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block"></span>Meta Ads
            </span>
          </h3>
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
            {/* Age Bar Chart */}
            {demographics.byAge?.length > 0 && (
              <div className="xl:col-span-7 bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                <h4 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary text-[18px]">bar_chart</span>
                  Distribuição por Faixa Etária
                </h4>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={demographics.byAge} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="age" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                    <RechartsTooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar yAxisId="left" dataKey="conversions" name="Conversões" fill="rgba(205,25,249,0.75)" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="right" dataKey="clicks" name="Cliques" fill="rgba(99,102,241,0.35)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            {/* Gender Pie Chart */}
            {demographics.byGender?.length > 0 && (
              <div className="xl:col-span-5 bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                <h4 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary text-[18px]">donut_large</span>
                  Conversões por Gênero
                </h4>
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={demographics.byGender}
                      dataKey="conversions"
                      nameKey="gender"
                      cx="50%" cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={3}
                    >
                      {demographics.byGender.map((entry, i) => (
                        <Cell key={i} fill={GENDER_COLORS[entry.gender] || '#94a3b8'} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', fontSize: 12 }}
                      formatter={(val, name) => [fmt(val, 1), GENDER_LABELS[name] || name]}
                    />
                    <Legend
                      formatter={(val) => GENDER_LABELS[val] || val}
                      wrapperStyle={{ fontSize: 11 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
            {/* Top Audiences Table */}
            {demographics.topAudiences?.length > 0 && (
              <div className="xl:col-span-12 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                <div className="p-5 border-b border-slate-100">
                  <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                    <span className="material-symbols-outlined text-primary text-[18px]">group</span>
                    Top Públicos que Mais Convertem
                  </h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-slate-50/50">
                      <tr>
                        {['#', 'Faixa Etária', 'Gênero', 'Conversões', 'Cliques', 'Investimento', 'Conv/Clique'].map(h => (
                          <th key={h} className="py-3 px-5 text-[10px] font-bold text-slate-500 uppercase tracking-widest whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {demographics.topAudiences.map((r, i) => {
                        const convRate = r.clicks > 0 ? ((r.conversions / r.clicks) * 100).toFixed(1) : '—';
                        return (
                          <tr key={i} className={`hover:bg-slate-50/80 ${i === 0 ? 'bg-primary/5' : ''}`}>
                            <td className="py-3 px-5">
                              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black ${i === 0 ? 'bg-primary text-white' : 'bg-slate-100 text-slate-600'}`}>{i + 1}</span>
                            </td>
                            <td className="py-3 px-5 font-bold text-slate-700">{r.age}</td>
                            <td className="py-3 px-5 text-slate-600">{GENDER_LABELS[r.gender] || r.gender}</td>
                            <td className="py-3 px-5 font-black text-primary">{fmt(r.conversions, 1)}</td>
                            <td className="py-3 px-5 text-slate-600">{Number(r.clicks || 0).toLocaleString('pt-BR')}</td>
                            <td className="py-3 px-5 font-bold text-slate-800">{fmtCurrency(r.spend)}</td>
                            <td className="py-3 px-5 text-slate-600">{convRate !== '—' ? `${convRate}%` : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Campaign Table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <h3 className="font-headline text-xl font-bold text-slate-800">Detalhamento por Campanha</h3>
          <div className="flex items-center gap-2 bg-slate-50 p-1 rounded-xl">
            {['ALL', 'meta', 'google'].map(src => (
              <button key={src} onClick={() => setSourceFilter(src)}
                className={`px-4 py-2 rounded-lg text-[11px] uppercase tracking-wider font-bold transition-all ${sourceFilter === src ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'}`}>
                {src === 'ALL' ? 'Todas Origens' : src === 'meta' ? 'Meta Ads' : 'Google Ads'}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50/50">
              <tr>
                <th className="py-4 px-6 text-[10px] font-bold text-slate-500 uppercase tracking-widest">Origem</th>
                <th className="py-4 px-6 text-[10px] font-bold text-slate-500 uppercase tracking-widest">Conta</th>
                <th className="py-4 px-6 text-[10px] font-bold text-slate-500 uppercase tracking-widest">Campanha</th>
                {[
                  { key: 'spend', label: 'Investimento' },
                  { key: 'impressions', label: 'Impressões' },
                  { key: 'clicks', label: 'Cliques' },
                  { key: 'conversions', label: isMessages ? 'Conversas' : 'Conversões' },
                ].map(col => (
                  <th key={col.key} onClick={() => handleSort(col.key)}
                    className="py-4 px-6 text-[10px] font-bold text-slate-500 uppercase tracking-widest text-right cursor-pointer hover:bg-slate-100 select-none transition-colors">
                    <div className="flex items-center justify-end gap-1">
                      {col.label}
                      <span className="material-symbols-outlined text-[13px] opacity-50">
                        {sortField === col.key ? (sortDir === 'desc' ? 'arrow_downward' : 'arrow_upward') : 'unfold_more'}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sorted.map((row, i) => {
                const cfg = SOURCE_CONFIG[row.source] || SOURCE_CONFIG.meta;
                return (
                  <tr key={i} className="hover:bg-slate-50/80 transition-colors">
                    <td className="py-4 px-6">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${cfg.color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`}></span>{cfg.label}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-xs font-bold text-slate-700 truncate max-w-[150px]">{row.accountName || row.accountId}</td>
                    <td className="py-4 px-6 text-slate-600 truncate max-w-[250px]">{row.campaignName}</td>
                    <td className="py-4 px-6 text-right font-bold text-slate-800">{fmtCurrency(row.spend)}</td>
                    <td className="py-4 px-6 text-right text-slate-600">{Number(row.impressions || 0).toLocaleString('pt-BR')}</td>
                    <td className="py-4 px-6 text-right text-slate-600">{Number(row.clicks || 0).toLocaleString('pt-BR')}</td>
                    <td className="py-4 px-6 text-right font-bold text-primary">{fmt(row.conversions, 1)}</td>
                  </tr>
                );
              })}
              {sorted.length === 0 && (
                <tr><td colSpan={7} className="py-16 text-center text-slate-400 text-sm">Nenhuma campanha encontrada.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const GoogleAdsSnapshotViewer = ({ data = [], summary = {}, entityName }) => {
  return (
    <div className="space-y-8 animate-fade-in">
      <h2 className="font-headline font-extrabold text-3xl text-on-surface">{entityName || 'Relatório Google Ads'}</h2>
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          {[
            { label: 'Investimento', value: fmtCurrency(summary.spend), icon: 'payments', color: 'text-blue-500', bg: 'bg-blue-50' },
            { label: 'Impressões', value: Number(summary.impressions || 0).toLocaleString('pt-BR'), icon: 'visibility', color: 'text-indigo-500', bg: 'bg-indigo-50' },
            { label: 'Cliques', value: Number(summary.clicks || 0).toLocaleString('pt-BR'), icon: 'ads_click', color: 'text-violet-500', bg: 'bg-violet-50' },
            { label: 'CTR Médio', value: `${fmt(summary.ctr)}%`, icon: 'percent', color: 'text-fuchsia-500', bg: 'bg-fuchsia-50' },
            { label: 'CPC Médio', value: fmtCurrency(summary.cpc), icon: 'monetization_on', color: 'text-rose-500', bg: 'bg-rose-50' },
            { label: 'Conversões', value: fmt(summary.conversions, 0), icon: 'check_circle', color: 'text-emerald-500', bg: 'bg-emerald-50' },
          ].map((kpi, idx) => (
            <div key={idx} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm flex flex-col items-center justify-center text-center">
              <div className={`w-12 h-12 rounded-full ${kpi.bg} flex items-center justify-center mb-3`}>
                <span className={`material-symbols-outlined ${kpi.color} text-2xl`}>{kpi.icon}</span>
              </div>
              <h4 className="text-2xl font-black font-headline text-slate-800 tracking-tight leading-none mb-1">{kpi.value}</h4>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-1">{kpi.label}</p>
            </div>
          ))}
        </div>
      )}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-slate-100">
          <h3 className="font-headline font-bold text-lg text-slate-800">Detalhamento de Performance</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50/50">
              <tr>
                {['Campanha', 'Investimento', 'Impressões', 'Cliques', 'CTR', 'CPC', 'Conversões'].map(h => (
                  <th key={h} className="py-3 px-5 text-[10px] font-bold text-slate-500 uppercase tracking-widest">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map((row, i) => (
                <tr key={i} className="hover:bg-slate-50/80">
                  <td className="py-3 px-5 font-bold text-slate-700 truncate max-w-[220px]">{row.name || row.campaignName || '—'}</td>
                  <td className="py-3 px-5 font-bold text-slate-800">{fmtCurrency(row.spend)}</td>
                  <td className="py-3 px-5 text-slate-600">{Number(row.impressions || 0).toLocaleString('pt-BR')}</td>
                  <td className="py-3 px-5 text-slate-600">{Number(row.clicks || 0).toLocaleString('pt-BR')}</td>
                  <td className="py-3 px-5 text-slate-600">{fmt(Number(row.ctr || 0) * 100)}%</td>
                  <td className="py-3 px-5 text-slate-600">{fmtCurrency(row.cpc)}</td>
                  <td className="py-3 px-5 font-bold text-primary">{fmt(row.conversions, 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const PageSnapshotViewer = ({ reportData, entityName }) => {
  if (!reportData) return null;
  return (
    <div className="space-y-8 animate-fade-in">
      <h2 className="font-headline font-extrabold text-3xl text-on-surface">{entityName || 'Relatório de Página'}</h2>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: 'Impressões', value: reportData.totals.impressions, icon: 'visibility', color: 'text-primary' },
          { label: 'Alcance', value: reportData.totals.reach, icon: 'public', color: 'text-blue-500' },
          { label: 'Engajamento', value: reportData.totals.engagement, icon: 'touch_app', color: 'text-purple-500' },
          { label: 'Visualizações', value: reportData.totals.views, icon: 'pageview', color: 'text-amber-500' },
          { label: 'Seguidores', value: reportData.fanCount, icon: 'groups', color: 'text-emerald-500' },
        ].map((kpi, i) => (
          <div key={i} className="bg-surface-container-lowest p-5 rounded-2xl border border-outline-variant/30 flex flex-col items-center text-center shadow-sm">
            <span className={`material-symbols-outlined ${kpi.color} mb-2 text-3xl`}>{kpi.icon}</span>
            <p className="text-xs font-bold text-secondary uppercase tracking-wider mb-1">{kpi.label}</p>
            <p className="font-headline font-extrabold text-2xl text-on-surface">{Number(kpi.value || 0).toLocaleString('pt-BR')}</p>
          </div>
        ))}
      </div>
      {reportData.chartData && reportData.chartData.length > 0 && (
        <div className="bg-surface-container-lowest p-6 rounded-2xl border border-outline-variant/30 shadow-sm">
          <h3 className="font-headline font-bold text-lg mb-6">Evolução do Desempenho</h3>
          <div className="h-[350px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={reportData.chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} dx={-10} />
                <RechartsTooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }} />
                <Legend wrapperStyle={{ paddingTop: '20px' }} />
                <Line type="monotone" name="Impressões" dataKey="page_impressions" stroke="#4f46e5" strokeWidth={3} dot={false} activeDot={{ r: 6 }} />
                <Line type="monotone" name="Alcance" dataKey="page_impressions_unique" stroke="#3b82f6" strokeWidth={3} dot={false} activeDot={{ r: 6 }} />
                <Line type="monotone" name="Engajamento" dataKey="page_post_engagements" stroke="#a855f7" strokeWidth={3} dot={false} activeDot={{ r: 6 }} />
                <Line type="monotone" name="Visualizações" dataKey="page_views_total" stroke="#f59e0b" strokeWidth={3} dot={false} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

const SharedReport = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [previousData, setPreviousData] = useState([]);
  const [campaignSummary, setCampaignSummary] = useState(null);
  const [secondaryInsights, setSecondaryInsights] = useState([]);
  const [clinicData, setClinicData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [reportConfig, setReportConfig] = useState(null);

  useEffect(() => {
    const fetchSharedReport = async () => {
      try {
        const response = await reportsApi.getSharedReport(token);
        if (response.success && response.data) {
          setData(response.data.insights || []);
          setPreviousData(response.data.previousInsights || []);
          setCampaignSummary(response.data.campaignSummary || null);
          setSecondaryInsights(response.data.secondaryInsights || []);
          setClinicData(response.data.clinicData || null);
          setReportConfig({
            reportType: response.data.reportType,
            options: response.data.options || { level: 'campaign', conversionType: 'lead' },
            entityName: response.data.entityName
          });
        } else {
          setError('Relatório não encontrado ou inválido.');
        }
      } catch (err) {
        setError(err.message || 'O link pode estar expirado ou inválido.');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchSharedReport();
    }
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex flex-col items-center justify-center font-body pb-20">
        <div className="w-16 h-16 border-4 border-primary/30 border-t-primary rounded-full animate-spin mb-4"></div>
        <p className="text-secondary font-medium">Carregando relatório seguro...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-surface flex flex-col items-center justify-center font-body p-6">
        <div className="bg-surface-container-lowest p-10 rounded-3xl shadow-xl max-w-md w-full text-center border border-outline-variant/30">
          <span className="material-symbols-outlined text-6xl text-red-400 mb-6">link_off</span>
          <h1 className="text-2xl font-bold font-headline text-on-surface mb-3">Link Indisponível</h1>
          <p className="text-secondary font-body mb-8">
            {error} <br/> O relatório pode ter expirado ou foi removido pelo proprietário.
          </p>
          <img src="/LOGO.png" alt="Lumina Reporting" className="h-8 w-auto mx-auto opacity-50 grayscale hover:grayscale-0 transition-all" />
        </div>
      </div>
    );
  }

  // Handle Export CSV stub (no export in public mode, or limited)
  const handleExportCsv = () => {
    // Basic CSV export logic could be here if we want public visitors to export
    alert("Exportação CSV disponível apenas na plataforma completa.");
  };

  return (
    <div className="min-h-screen bg-surface font-body overflow-x-hidden relative">
      <header className="bg-surface-container-lowest border-b border-outline-variant/30 px-6 lg:px-12 py-4 flex justify-between items-center sticky top-0 z-50">
        <div className="flex items-center gap-4">
          <img src="/LOGO.png" alt="Lumina Reporting" className="h-8 w-auto border-r border-outline-variant/30 pr-4" />
          <div className="hidden sm:block">
            <h1 className="font-extrabold font-headline text-on-surface text-sm">Relatório Publicado</h1>
            <p className="text-xs text-primary font-bold">{reportConfig?.entityName || 'Conta de Anúncios'}</p>
          </div>
          {reportConfig?.options?.startDate && (
            <span className="hidden md:inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold">
              <span className="material-symbols-outlined text-[14px]">calendar_month</span>
              {fmtDate(reportConfig.options.startDate)} — {fmtDate(reportConfig.options.endDate)}
            </span>
          )}
        </div>
        <div className="text-xs text-secondary font-medium bg-surface-container px-3 py-1.5 rounded-full flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span> Visualização Segura em Tempo Real
        </div>
      </header>

        <main className={`px-4 lg:px-12 py-8 mx-auto w-full ${reportConfig?.reportType === 'clinic_funnel' ? 'max-w-[1920px]' : 'max-w-[1400px]'}`}>
          {reportConfig?.reportType === 'franchise_v2' ? (
            <FranchiseV2ResultView
              units={reportConfig.options.units}
              period={reportConfig.options.period}
              topCampaigns={reportConfig.options.topCampaigns}
              topAds={reportConfig.options.topAds}
              clinicSnapshotData={reportConfig.options.snapshotClinicData}
              readOnly={true}
            />
          ) : reportConfig?.options?.franchiseData ? (
          <FranchiseReportVisualization
            data={reportConfig.options.franchiseData}
            reportName={reportConfig.entityName || 'Relatório de Franquia'}
            periodLabel={reportConfig.options.periodLabel || ''}
            readOnly={true}
            onBack={() => {}}
            clinicSection={<ClinicDataSection data={reportConfig.options.snapshotClinicData} readOnly />}
          />
        ) : reportConfig?.reportType === 'combined' && reportConfig?.options?.snapshotData ? (
          <CombinedSnapshotViewer
            data={reportConfig.options.snapshotData}
            summary={reportConfig.options.snapshotSummary}
            entityName={reportConfig.entityName}
            demographics={reportConfig.options.snapshotDemographics}
            topCreatives={reportConfig.options.snapshotTopCreatives || []}
            accountsStatus={reportConfig.options.snapshotAccountsStatus || []}
            objective={reportConfig.options.snapshotObjective || 'ALL'}
            startDate={reportConfig.options.startDate}
            endDate={reportConfig.options.endDate}
            showFunnelAndGoals={reportConfig?.options?.showFunnelAndGoals !== false}
            leadGoalsSection={reportConfig?.options?.showFunnelAndGoals !== false && reportConfig.options.snapshotLeadGoals?.data ? (
              // Ritmo vem do asOf congelado no snapshot, nao do "hoje" de quem abre o link
              <LeadGoalsBlock
                readOnly
                data={reportConfig.options.snapshotLeadGoals.data}
                type={reportConfig.options.snapshotLeadGoals.type || 'message'}
                unmappedCount={reportConfig.options.snapshotLeadGoals.unmappedAccounts || 0}
              />
            ) : null}
            leadGoal={reportConfig?.options?.showFunnelAndGoals !== false ? reportConfig?.options?.snapshotLeadGoal : null}
            leadGoalAchieved={reportConfig?.options?.snapshotLeadGoalAchieved || 0}
            clinicSection={
              <ClinicDataSection
                data={reportConfig.options.snapshotClinicData}
                campaignSpend={reportConfig.options.snapshotSummary?.totalSpend}
                readOnly
              />
            }
          />
        ) : reportConfig?.reportType === 'google_ads' && reportConfig?.options?.snapshotData ? (
          <GoogleAdsSnapshotViewer
            data={reportConfig.options.snapshotData}
            summary={reportConfig.options.snapshotSummary}
            entityName={reportConfig.entityName}
          />
        ) : reportConfig?.reportType === 'page' && reportConfig?.options?.reportData ? (
          <PageSnapshotViewer
            reportData={reportConfig.options.reportData}
            entityName={reportConfig.entityName}
          />
        ) : reportConfig?.reportType === 'clinic_routine' ? (
          <ClinicRoutineReportVisualization
            routineName={reportConfig.entityName || reportConfig.options?.routineName}
            adAccountName={reportConfig.options?.adAccountName}
            pageName={reportConfig.options?.pageName}
            periodStart={reportConfig.options?.periodStart}
            periodEnd={reportConfig.options?.periodEnd}
            summary={reportConfig.options?.snapshotSummary}
            collections={reportConfig.options?.snapshotCollections || []}
            readOnly={true}
          />
        ) : reportConfig?.reportType === 'clinic_funnel' ? (
          <ClinicFunnelOverviewVisualization
            report={reportConfig.options?.funnelReport}
            showFunnelAndGoals={reportConfig?.options?.showFunnelAndGoals !== false}
          />
        ) : reportConfig?.reportType === 'clinic_multi' ? (
          <ClinicMultiUnitReportVisualization
            groupName={reportConfig.entityName || reportConfig.options?.groupName}
            periodStart={reportConfig.options?.periodStart}
            periodEnd={reportConfig.options?.periodEnd}
            unitsWithData={reportConfig.options?.unitsWithData || []}
            unitsNoData={reportConfig.options?.unitsNoData || []}
            readOnly={true}
            showFunnelAndGoals={reportConfig?.options?.showFunnelAndGoals !== false}
          />
        ) : (
          <ReportVisualization
            data={data}
            previousData={previousData}
            secondaryData={secondaryInsights}
            level={reportConfig?.options?.level || 'campaign'}
            conversionType={reportConfig?.options?.conversionType || 'lead'}
            onBack={() => {}}
            onExportCsv={handleExportCsv}
            campaignSummary={campaignSummary}
            showFunnelAndGoals={reportConfig?.options?.showFunnelAndGoals !== false}
            leadGoal={reportConfig?.options?.showFunnelAndGoals !== false ? (reportConfig?.options?.leadGoalSnapshot?.goal || null) : null}
            leadGoalAchieved={reportConfig?.options?.showFunnelAndGoals !== false ? (reportConfig?.options?.leadGoalSnapshot?.achieved || 0) : 0}
            leadGoalLegend={reportConfig?.options?.showFunnelAndGoals !== false ? (reportConfig?.options?.leadGoalSnapshot?.legend || null) : null}
            readOnly={true}
            clinicSection={
              <ClinicDataSection
                data={clinicData}
                campaignSpend={(data || []).reduce((s, r) => s + parseFloat(r.spend || 0), 0)}
                readOnly
              />
            }
          />
        )}
      </main>
      
      <footer className="py-6 text-center text-xs text-secondary/60">
        <p>&copy; {new Date().getFullYear()} Lumina Premium Reporting. Todos os direitos reservados.</p>
      </footer>
    </div>
  );
};

export default SharedReport;
